/**
 * **The settings hub's cards and a settings page's sections** (W28, FR-65,
 * plan D84) — copy-adapted from ProDesk's (`repo:prodesk:apps/web/src/components/HubCard.tsx`
 * and `repo:prodesk:apps/web/src/routes/Firm/settings/SettingsPage.tsx`).
 *
 * - `HubCard` — one card per page the hub opens: an icon, a title, what the
 *   page is for, and **one line of what is set now** under a rule — a skeleton
 *   while it loads, *Open to see what’s set* when it could not be read, never a
 *   stale or invented line. Hover lifts it.
 * - `HubGroup` — the labelled section a hub groups its cards under.
 * - `HubRow` — one full-width link in the card's manner; with `external` it
 *   leaves for another app (the FiveOX console, FR-37).
 * - `SettingsSection` — a section of a settings page: a heading with an icon,
 *   one line of what it is for, and a body, drawn as a card so a long page
 *   reads as a set of answerable questions rather than one long form.
 * - `SwitchRow` — one yes-or-no question in a section, ProDesk's
 *   (`repo:prodesk:apps/web/src/routes/Firm/settings/Billing.tsx`): the switch
 *   is part of the page's draft and waits for the bar's Save, as ProDesk's do.
 */
import { Skeleton, Switch, TONE_TEXT } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import { ChevronRight, CircleAlert, CircleCheck, CircleDashed, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

/** A card's line: what is set, something to look at, or nothing set yet. */
export interface SummaryLine {
  line: string
  tone: 'set' | 'attention' | 'unset'
}

const SURFACE = 'border-border rounded-2xl border bg-white shadow-sm'
const LIFT = 'group hover:border-primary/40 focus-visible:ring-primary/40 transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none'
const ICON_TILE = 'bg-primary/10 text-primary group-hover:bg-primary flex shrink-0 items-center justify-center rounded-xl transition-colors group-hover:text-white'

export function LineIcon({ tone }: { tone: SummaryLine['tone'] }) {
  if (tone === 'attention') return <CircleAlert size={14} className={`shrink-0 ${TONE_TEXT.warning}`} aria-hidden />
  if (tone === 'unset') return <CircleDashed size={14} className="text-muted-foreground shrink-0" aria-hidden />
  return <CircleCheck size={14} className="text-primary shrink-0" aria-hidden />
}

export interface HubCardProps {
  /** `data-card` and `data-summary` — what a test reads. */
  cardKey: string
  href: string
  icon: LucideIcon
  title: string
  description: string
  line: SummaryLine | null
  loading: boolean
}

export function HubCard({ cardKey, href, icon: Icon, title, description, line, loading }: HubCardProps) {
  return (
    <Link to={href} data-card={cardKey} className={`${SURFACE} ${LIFT} flex h-full min-w-0 flex-col p-5`}>
      <div className="flex items-start gap-4">
        <span className={`${ICON_TILE} h-11 w-11`}>
          <Icon size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-foreground text-base font-semibold">{title}</h3>
            <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{description}</p>
        </div>
      </div>
      <div className="mt-auto pt-4">
        <div className="border-border border-t pt-3">
          {loading ? (
            <Skeleton className="h-4 w-2/3" />
          ) : line ? (
            <p data-summary={cardKey} className={`flex items-center gap-2 text-sm ${line.tone === 'unset' ? 'text-muted-foreground' : 'text-foreground'}`}>
              <LineIcon tone={line.tone} />
              <span className="min-w-0 truncate">{line.line}</span>
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">Open to see what’s set</p>
          )}
        </div>
      </div>
    </Link>
  )
}

/** A hub's labelled group: a small caps heading, one line, and the grid. */
export function HubGroup({
  id,
  title,
  description,
  children,
  columns = 3,
}: {
  id: string
  title: string
  description?: string
  children: ReactNode
  /** Cards across at the widest. 3 by default; 2 for a group of two or four, which would otherwise leave a hole. */
  columns?: 2 | 3
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {title}
        </h2>
        {description && <p className="text-muted-foreground text-sm">{description}</p>}
      </div>
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${columns === 3 ? 'xl:grid-cols-3' : ''}`}>{children}</div>
    </section>
  )
}

/** One full-width link in the hub card's manner. `external` leaves the app (a new tab is not opened: the console is the same estate). */
export function HubRow({
  rowKey,
  href,
  icon: Icon,
  title,
  description,
  external = false,
}: {
  rowKey: string
  href: string
  icon: LucideIcon
  title: string
  description: ReactNode
  external?: boolean
}) {
  const body = (
    <>
      <span className={`${ICON_TILE} h-10 w-10`}>
        <Icon size={18} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-sm font-semibold">{title}</span>
        <span className="text-muted-foreground block text-sm">{description}</span>
      </span>
      {external ? (
        <ExternalLink size={16} className="text-muted-foreground group-hover:text-primary shrink-0" aria-hidden />
      ) : (
        <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
      )}
    </>
  )
  const className = `${SURFACE} ${LIFT} flex min-w-0 items-center gap-4 p-4 sm:p-5`
  return external ? (
    <a href={href} data-row={rowKey} className={className}>
      {body}
    </a>
  ) : (
    <Link to={href} data-row={rowKey} className={className}>
      {body}
    </Link>
  )
}

/** A section of a settings page: a heading with an icon, one line of what it is for, and a body. */
export function SettingsSection({
  id,
  icon,
  title,
  description,
  children,
  footer,
  action,
}: {
  id?: string
  icon?: ReactNode
  title: string
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** One small control at the header's right — *Change…* for a step-up act. */
  action?: ReactNode
}) {
  return (
    <section id={id} aria-labelledby={id ? `${id}-title` : undefined} className={`${SURFACE} scroll-mt-24`}>
      <header className="border-border flex items-start gap-3 border-b px-5 py-4 sm:px-6">
        {icon && <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">{icon}</span>}
        <div className="min-w-0 flex-1">
          <h2 id={id ? `${id}-title` : undefined} className="text-foreground text-base font-semibold">
            {title}
          </h2>
          {description && <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className="px-5 py-5 sm:px-6">{children}</div>
      {footer && <footer className="border-border border-t px-5 py-3 sm:px-6">{footer}</footer>}
    </section>
  )
}

/** One yes-or-no question: the question and what each answer means on the left, its switch on the right. Rows stack in a `divide-y` list. */
export function SwitchRow({
  title,
  hint,
  srLabel,
  checked,
  disabled,
  onChange,
}: {
  title: string
  hint?: ReactNode
  /** The switch's name when it must say more than the title; the title otherwise. */
  srLabel?: string
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-foreground text-sm font-medium">{title}</p>
        {hint && <p className="text-muted-foreground mt-0.5 text-sm">{hint}</p>}
      </div>
      <Switch checked={checked} disabled={disabled} srLabel={srLabel ?? title} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 shrink-0" />
    </div>
  )
}
